/**
 * Letters — /admin/letters
 *
 * Wave 6 Batch 3 · Cluster A — Log-Style chrome sweep.
 * Anchor: CreditEngineEvents (Wave 5-Late).
 *
 *   • AdminLayout wrap (unchanged).
 *   • `_primitives.Section` shells for filters + letters table.
 *   • `_primitives.TextInput` (search) + `SelectInput` (bureau/status/type).
 *   • `_primitives.GhostButton` for modal Download buttons.
 *   • Pill pastel color arg REMOVED. Emits DS `Badge` with `shape="sharp"`
 *     + semantic tone mapping:
 *       - letter_type   → accent
 *       - finalized     → successMuted
 *       - draft         → warning
 *       - AI-drafted    → accent
 *   • Dense hairline table `px-4 py-1.5` + `isLast` guard.
 *   • Modal chrome swept to `--ds-*`.
 *
 * User PII rendered on this surface (email, recipient name) — chrome ONLY.
 * All testids + API paths + filter payloads preserved BYTE-EXACT.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Download, Search, X } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { Badge } from "@/design-system/components/Badge";
import { Heading } from "@/design-system/components/Heading";
import { api, formatApiError } from "@/api/client";
import { Section, TextInput, SelectInput, GhostButton } from "@/admin/_primitives";

const BUREAUS = ["experian", "equifax", "transunion"];
const STATUSES = ["draft", "finalized"];

const STATUS_TONE = {
  finalized: "successMuted",
  draft:     "warning",
};

function LetterTypePill({ value }) {
  if (!value) return <span style={{ color: "var(--ds-color-ink-subtle)" }}>—</span>;
  return (
    <Badge tone="accent" size="sm" shape="sharp" className="uppercase tracking-[0.1em]">
      {value}
    </Badge>
  );
}

function StatusPill({ value }) {
  if (!value) return <span style={{ color: "var(--ds-color-ink-subtle)" }}>—</span>;
  return (
    <Badge
      tone={STATUS_TONE[value] || "neutralMuted"}
      size="sm"
      shape="sharp"
      className="uppercase tracking-[0.1em]"
    >
      {value}
    </Badge>
  );
}

export default function AdminLetters() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [bureau, setBureau] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [letterType, setLetterType] = useState("");
  const [previewId, setPreviewId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/dispute-letters");
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Build option lists from data.
  const letterTypes = useMemo(() => {
    const s = new Set();
    items.forEach((i) => i.letter_type && s.add(i.letter_type));
    return Array.from(s).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const term = debouncedQ.trim().toLowerCase();
    return items.filter((row) => {
      if (term && !(`${row.user_email || ""} ${row.recipient_name || ""}`).toLowerCase().includes(term)) return false;
      if (bureau && row.bureau !== bureau) return false;
      if (statusFilter && row.status !== statusFilter) return false;
      if (letterType && row.letter_type !== letterType) return false;
      return true;
    });
  }, [items, debouncedQ, bureau, statusFilter, letterType]);

  return (
    <AdminLayout
      title="Letters"
      subtitle={`${items.length} dispute letter${items.length === 1 ? "" : "s"} on record`}
    >
      <div className="mb-3 max-w-3xl">
        <AdminPageHelp
          title="Letters"
          purpose="View every dispute letter drafted by members plus manual review + moderation controls."
          configures="Approve, reject, or flag drafted letters for internal review."
          impact="Approved letters are eligible for the member to mail. Rejected letters are hidden from the member's letter list."
          safety="Every state change is logged in AuditLogs. Rejected letters can be re-approved."
        />
      </div>

      <Section title="Filter" testid="letters-filter-section">
        <div className="grid md:grid-cols-[1fr_auto_auto_auto] gap-2 items-end">
          <label className="block relative">
            <Search
              className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              style={{ color: "var(--ds-color-ink-subtle)" }}
              aria-hidden="true"
            />
            <TextInput
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by user email or recipient…"
              data-testid="letters-search"
              className="w-full pl-9"
            />
          </label>
          <SelectInput
            value={bureau}
            onChange={(e) => setBureau(e.target.value)}
            data-testid="letters-filter-bureau"
          >
            <option value="">All bureaus</option>
            {BUREAUS.map((b) => <option key={b} value={b}>{b}</option>)}
          </SelectInput>
          <SelectInput
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="letters-filter-status"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </SelectInput>
          <SelectInput
            value={letterType}
            onChange={(e) => setLetterType(e.target.value)}
            data-testid="letters-filter-type"
          >
            <option value="">All types</option>
            {letterTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </SelectInput>
        </div>
      </Section>

      {loading ? (
        <div className="text-[13px] py-6 text-center" style={{ color: "var(--ds-color-ink-muted)" }}>
          Loading…
        </div>
      ) : (
        <Section
          title="Letters"
          testid="letters-section"
          bodyClassName="p-0"
          badge={
            <span
              className="text-[11px] uppercase tracking-[0.14em] tabular-nums"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              {filtered.length} showing · {items.length} total
            </span>
          }
        >
          <div className="overflow-x-auto" data-testid="letters-table">
            <table className="w-full text-[13px]">
              <thead>
                <tr
                  className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                  style={{
                    color: "var(--ds-color-ink-subtle)",
                    background: "var(--ds-color-admin-surface-muted)",
                    borderBottom: "1px solid var(--ds-color-admin-border)",
                  }}
                >
                  <th className="px-4 py-2">User</th>
                  <th className="px-4 py-2">Letter type</th>
                  <th className="px-4 py-2">Recipient</th>
                  <th className="px-4 py-2 text-right">Items</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Downloads</th>
                  <th className="px-4 py-2">Generated</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, idx) => {
                  const isLast = idx === filtered.length - 1;
                  const dl = row.downloads || {};
                  const dlSum = (dl.pdf || 0) + (dl.docx || 0) + (dl.txt || 0);
                  return (
                    <tr
                      key={row.id}
                      data-testid={`letters-row-${row.id}`}
                      className="cursor-pointer transition-colors"
                      onClick={() => setPreviewId(row.id)}
                      style={{
                        borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)",
                        transitionDuration: "var(--ds-duration-fast)",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "var(--ds-color-admin-surface-muted)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <td
                        className="px-4 py-1.5 text-[12px] truncate max-w-[220px]"
                        style={{ color: "var(--ds-color-ink)" }}
                      >
                        {row.user_email || "—"}
                      </td>
                      <td className="px-4 py-1.5">
                        <LetterTypePill value={row.letter_type} />
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px] truncate max-w-[180px]"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.recipient_name || "—"}
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px] text-right tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.item_count}
                      </td>
                      <td className="px-4 py-1.5">
                        <StatusPill value={row.status} />
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px] text-right tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {dlSum}
                      </td>
                      <td
                        className="px-4 py-1.5 text-[12px] tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.generated_at?.slice ? row.generated_at.slice(0, 10) : String(row.generated_at || "").slice(0, 10)}
                      </td>
                      <td className="px-4 py-1.5 text-right">
                        <button
                          data-testid={`letters-open-${row.id}`}
                          onClick={(e) => { e.stopPropagation(); setPreviewId(row.id); }}
                          className="text-[11px] uppercase tracking-[0.12em] font-semibold hover:underline"
                          style={{ color: "var(--ds-color-accent-strong)" }}
                        >
                          Preview
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-4 py-8 text-center text-[13px]"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      No letters match.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {previewId && <LetterPreview letterId={previewId} onClose={() => setPreviewId(null)} />}
    </AdminLayout>
  );
}

function LetterPreview({ letterId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/admin/dispute-letters/${letterId}`).then((r) => {
      if (cancel) return;
      setDetail(r.data);
    }).catch((e) => toast.error(formatApiError(e))).finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [letterId]);

  async function download(fmt) {
    try {
      const res = await api.get(`/admin/dispute-letters/${letterId}/download?format=${fmt}`, { responseType: "blob" });
      const mime = res.headers["content-type"] || "application/octet-stream";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `dispute-letter-${letterId}.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fmt.toUpperCase()}`);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div
      data-testid="letters-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(27, 26, 22, 0.55)" }}
      onClick={onClose}
    >
      <div
        data-testid="letters-modal"
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-[var(--ds-radius-card)]"
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-admin-border)",
          boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="sticky top-0 flex items-center justify-between px-5 py-3.5 z-10 border-b"
          style={{
            background: "var(--ds-color-surface)",
            borderColor: "var(--ds-color-admin-border)",
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={onClose}
              data-testid="letters-modal-back"
              className="inline-flex items-center gap-1 text-[12px] uppercase tracking-[0.12em] transition-colors"
              style={{ color: "var(--ds-color-ink-muted)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to list
            </button>
            <div className="min-w-0">
              <div
                className="text-[10px] uppercase tracking-[0.18em] font-semibold"
                style={{ color: "var(--ds-color-accent-strong)" }}
              >
                Letter preview
              </div>
              <Heading level={2} size="md" weight="semibold" className="truncate">
                {detail?.letter_type} → {detail?.recipient_name}
              </Heading>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {["pdf", "docx", "txt"].map((fmt) => (
              <GhostButton
                key={fmt}
                type="button"
                data-testid={`letters-download-${fmt}`}
                onClick={() => download(fmt)}
              >
                <Download className="w-3 h-3" /> {fmt.toUpperCase()}
              </GhostButton>
            ))}
            <button
              data-testid="letters-modal-close"
              onClick={onClose}
              className="ml-1 transition-colors"
              style={{ color: "var(--ds-color-ink-muted)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {loading && (
            <div className="text-sm" style={{ color: "var(--ds-color-ink-muted)" }}>Loading…</div>
          )}
          {detail && (
            <>
              <div className="grid grid-cols-2 gap-3 text-xs" style={{ color: "var(--ds-color-ink-muted)" }}>
                <Meta label="User" value={detail.user_email} />
                <Meta label="Status" value={detail.status} />
                <Meta label="Generated" value={(detail.generated_at || "").slice(0, 19)} />
                <Meta label="Bureau" value={detail.letter_type?.split("_").pop()} />
              </div>
              <div
                className="rounded-[var(--ds-radius-input)] p-4 text-sm whitespace-pre-wrap leading-relaxed"
                style={{
                  border: "1px solid var(--ds-color-admin-border)",
                  color: "var(--ds-color-ink)",
                }}
              >
                <strong
                  className="text-xs uppercase tracking-[0.15em] font-semibold"
                  style={{ color: "var(--ds-color-ink-subtle)" }}
                >
                  Recipient address
                </strong>
                <div className="mt-1">{detail.recipient_address}</div>
                <hr className="my-3" style={{ borderColor: "var(--ds-color-admin-border)" }} />
                <strong
                  className="text-xs uppercase tracking-[0.15em] font-semibold"
                  style={{ color: "var(--ds-color-ink-subtle)" }}
                >
                  Subject
                </strong>
                <div className="mt-1">{detail.subject_line}</div>
                <hr className="my-3" style={{ borderColor: "var(--ds-color-admin-border)" }} />
                <strong
                  className="text-xs uppercase tracking-[0.15em] font-semibold"
                  style={{ color: "var(--ds-color-ink-subtle)" }}
                >
                  Letter body
                </strong>
                <p className="mt-2 leading-relaxed">{detail.intro_paragraph}</p>
                <ol className="list-decimal list-inside space-y-3 mt-3">
                  {(detail.disputed_items || []).map((item, i) => (
                    <li key={i} style={{ color: "var(--ds-color-ink)" }}>
                      <strong>{item.creditor_name || item.bureau || "Item"}</strong>
                      {item.ai_drafted_reason && (
                        <span className="ml-2">
                          <Badge tone="accent" size="sm" shape="sharp" className="uppercase tracking-[0.1em]">
                            AI-drafted
                          </Badge>
                        </span>
                      )}
                      <div
                        className="text-sm mt-1"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {item.dispute_reason || item.reason}
                      </div>
                    </li>
                  ))}
                </ol>
                <p className="mt-4 leading-relaxed">{detail.request_paragraph}</p>
                {(detail.attachment_checklist || []).length > 0 && (
                  <>
                    <hr className="my-3" style={{ borderColor: "var(--ds-color-admin-border)" }} />
                    <strong
                      className="text-xs uppercase tracking-[0.15em] font-semibold"
                      style={{ color: "var(--ds-color-ink-subtle)" }}
                    >
                      Attachment checklist
                    </strong>
                    <ul className="list-disc list-inside mt-1 text-sm" style={{ color: "var(--ds-color-ink-muted)" }}>
                      {detail.attachment_checklist.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                  </>
                )}
                <hr className="my-3" style={{ borderColor: "var(--ds-color-admin-border)" }} />
                <div style={{ color: "var(--ds-color-ink-muted)" }}>{detail.signature_line}</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div
        className="text-[10px] uppercase tracking-[0.12em] font-semibold"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </div>
      <div className="text-sm" style={{ color: "var(--ds-color-ink)" }}>
        {value || "—"}
      </div>
    </div>
  );
}
